import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { Redirect, useHistory } from 'react-router';
import { get } from 'lodash';
import { Text, Button, Icon } from '@chaser/components';
import LoadingMessage from '../../components/LoadingMessage/index';
import CloseIcon from '../../assets/receivables/ico-close-red.svg';
import usePush from '../../hooks/usePush';
import AuthenticationService from '../../services/authentication-service';
import { setUserData, setUserLocalStorage } from '../login/duck/actions/login';
import { login } from '../login/duck/actions/login';
import style from './LoginIntuit.module.scss';
import Logo from '../../components/Logo';
import OrganisationService from '../../services/organisation-service';
import { useCacheActions } from '../../hooks/useFetch';
import { errors } from '@chaser/codes';
import { pendo } from '../../util/logging';

const LoginIntuitCallback = ({ location: { search } }) => {
  const dispatch = useDispatch();
  const { isLoggedIn, features } = useSelector(state => state.loginReducer);
  const history = useHistory();
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const { execute, loading, error, reset } = usePush(AuthenticationService.loginIntuitCallback);
  const { invalidateEndpoint } = useCacheActions();

  const apiCall = async password => {
    try {
      const [err, result] = await execute(search, password);

      if (err) {
        if (/exists/i.test(err.message)) {
          reset();
          setShowPasswordInput(true);
          return;
        }

        if (err.code === errors.INTUIT_EMAIL_NOT_VERIFIED) {
          history.push('/quickbooks/email-not-verified');
          return;
        }

        if (!showPasswordInput) {
          history.push('/', { loginError: 'Unable to authenticate using Intuit.' });
          return;
        }
        return;
      }

      if (!result) {
        console.error('Missing result from execute call');
        history.push('/', { loginError: 'Unable to authenticate using Intuit.' });
        return;
      }

      const { data } = result;

      // Handle the case where data.loginIntuitCallback might be undefined in tests
      if (!data || !data.loginIntuitCallback) {
        console.error('Missing data.loginIntuitCallback in response');
        history.push('/', { loginError: 'Unable to authenticate using Intuit.' });
        return;
      }

      const {
        user,
        organisation,
        token: userToken,
        isExternalAuthentication,
        isDisconnected,
      } = data.loginIntuitCallback;

      user.token = userToken;
      user.isExternalAuthentication = isExternalAuthentication;
      dispatch(setUserData(user));

      pendo.track('integration_callback_completed', {
        integration: 'intuit',
        isDisconnected,
        hasCompletedSetup: !!organisation?.hasCompletedSetup,
      });

      function getRoute(organisation, isDisconnected) {
        if (isDisconnected) {
          return '/settings/integrations';
        }
        if (get(organisation, 'hasCompletedSetup') === true) {
          return `/o/${organisation.ShortCode}`;
        } else if (get(organisation, 'hasCompletedSetup') === false) {
          return `/?organisation=${organisation.ShortCode}`;
        }
        return '/';
      }
      const route = getRoute(organisation, isDisconnected);

      await invalidateEndpoint(OrganisationService.get);
      setUserLocalStorage(user);
      dispatch(login());
      history.push(route);
    } catch (error) {
      console.error('Error in apiCall:', error);
      history.push('/', { loginError: 'An unexpected error occurred.' });
    }
  };

  useEffect(() => {
    const shouldStay = isLoggedIn && features?.intuitAuthMerged;
    if (shouldStay) {
      history.replace('/');
    } else if (search) {
      apiCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!search) return <Redirect to="/" />;

  if (showPasswordInput) {
    return (
      <div className={style.loginPageBackground}>
        <div className={style.loginPageFormContainer}>
          <Formik
            initialValues={{
              password: '',
            }}
            onSubmit={async (values, actions) => {
              await apiCall(values.password);
              actions.setSubmitting(false);
            }}
          >
            {({ values }) => (
              <Form className={style.loginFormContainer}>
                <div className={style.loginFormHead}>
                  <Logo />
                </div>
                <div className={style.loginFormBody}>
                  <h2 className={style.loginFormTitle}>Link your account</h2>
                  {error && (
                    <div className={style.loginErrorMessage}>
                      <p className={style.loginErrorText}>Sorry that password did not work.</p>
                      <button type="button" className={style.loginErrorIcon} onClick={reset}>
                        <img alt="" src={CloseIcon} />
                      </button>
                    </div>
                  )}
                  <div className={style.loginFormInputBlock} style={{}}>
                    <label className={style.loginFormLabel} htmlFor="password">
                      <Icon name="lock" size={20} />
                    </label>
                    <Field
                      name="password"
                      className={style.loginFormInput}
                      type="password"
                      autoComplete="off"
                      id="password"
                      placeholder="Enter password"
                    />
                  </div>
                  <p>
                    <Text as="span">
                      A Chaser account already exists with this email address. Enter your password
                      to link it with your Intuit sign-in &mdash; you won&apos;t need to do this
                      again.
                    </Text>
                  </p>

                  <Button
                    loading={loading}
                    disabled={values.password === ''}
                    type="submit"
                    className={style.loginFormButtonSubmit}
                    data-trackingid="integration-intuit-link-accounts"
                  >
                    Link accounts
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }

  return <LoadingMessage center useLogoAnimation />;
};

LoginIntuitCallback.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default LoginIntuitCallback;
